/**
 * Download Auteur Bridge card for Windows.
 *
 * Campaign `bridge-download-pairing-v3-20261015`, task BO05 (BO-1, BO-2, BO-5, BO-12, BO-15, BO-16).
 * Mounted inside Settings > Local Node and the /welcome Subscriptions details block.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'wouter';

import {
  fetchLatestBridgeRelease,
  requestBridgeDownloadGrant,
  type BridgeDownloadGrant,
  type BridgeRelease,
} from '../../api/bridgeReleases';
import { ApiClientError } from '../../api/http';

export type CardState = 'loading' | 'ready' | 'not_on_plan' | 'not_available' | 'error';

export interface BridgeDownloadCardProps {
  getLatestRelease?: () => Promise<BridgeRelease>;
  getDownloadGrant?: () => Promise<BridgeDownloadGrant>;
}

function classifyBridgeError(
  error: unknown,
  anyNotFoundStatusIsUnavailable: boolean,
): Exclude<CardState, 'loading' | 'ready'> {
  const status = (error as { status?: number } | null)?.status;
  const code = error instanceof ApiClientError ? error.code : '';

  if (status === 403 || code === 'not_entitled' || code === 'plan_required') {
    return 'not_on_plan';
  }
  if (
    status === 503 ||
    (anyNotFoundStatusIsUnavailable && status === 404) ||
    code === 'not_found' ||
    code === 'release_not_found' ||
    code === 'not_available'
  ) {
    return 'not_available';
  }
  return 'error';
}

export function classifyReleaseLoadError(
  error: unknown,
): Exclude<CardState, 'loading' | 'ready'> {
  return classifyBridgeError(error, true);
}

export function classifyDownloadGrantError(
  error: unknown,
): Exclude<CardState, 'loading' | 'ready'> {
  return classifyBridgeError(error, false);
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function BridgeDownloadCard({
  getLatestRelease = fetchLatestBridgeRelease,
  getDownloadGrant = requestBridgeDownloadGrant,
}: BridgeDownloadCardProps = {}) {
  const [cardState, setCardState] = useState<CardState>('loading');
  const [release, setRelease] = useState<BridgeRelease | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const loadRelease = useCallback(async () => {
    setCardState('loading');
    setError('');
    try {
      const data = await getLatestRelease();
      if (!data || !data.version) {
        setCardState('not_available');
        setRelease(null);
        return;
      }
      setRelease(data);
      setCardState('ready');
    } catch (err: unknown) {
      setRelease(null);
      const failureState = classifyReleaseLoadError(err);
      if (failureState === 'error') {
        setError(err instanceof Error ? err.message : 'Could not check for the latest release.');
      }
      setCardState(failureState);
    }
  }, [getLatestRelease]);

  useEffect(() => {
    void loadRelease();
  }, [loadRelease]);

  function handleCopyChecksum() {
    if (!release?.sha256) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(release.sha256).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {
        // Clipboard access failure is non-fatal.
      });
    }
  }

  async function handleDownload() {
    setDownloadError('');
    setDownloading(true);
    try {
      const grant = await getDownloadGrant();
      // BO-2, BO-15: The grant URL is short-lived and never logged or stored.
      const downloadAnchor = document.createElement('a');
      downloadAnchor.href = grant.url;
      if (grant.file_name) {
        downloadAnchor.download = grant.file_name;
      }
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      document.body.removeChild(downloadAnchor);
    } catch (err: unknown) {
      const failureState = classifyDownloadGrantError(err);
      if (failureState === 'not_on_plan' || failureState === 'not_available') {
        setDownloadError('');
        setCardState(failureState);
      } else {
        setDownloadError('Could not start the download. Please try again.');
      }
    } finally {
      setDownloading(false);
    }
  }

  const isUnsignedBeta = release?.code_signing_label === 'unsigned-beta' || release?.channel === 'beta-unsigned';

  return (
    <article className="settings-card bridge-download-card">
      <div className="settings-card-heading">
        <div>
          <h3>Download Auteur Bridge for Windows</h3>
          <p className="settings-muted">
            Connect your computer to DaVinci Resolve Studio and your local project folders.
          </p>
        </div>
      </div>

      {cardState === 'loading' && (
        <p role="status">Checking for the latest release…</p>
      )}

      {cardState === 'not_on_plan' && (
        <p className="notice">
          Your plan does not include the local bridge. <Link to="/billing">Upgrade</Link>
        </p>
      )}

      {cardState === 'not_available' && (
        <div className="bridge-download-error">
          <p role="status">Auteur Bridge is not available yet.</p>
          <button type="button" className="secondary" onClick={() => void loadRelease()}>
            Retry
          </button>
        </div>
      )}

      {cardState === 'error' && (
        <div className="bridge-download-error" role="alert">
          <p className="error">{error || 'Could not load release information.'}</p>
          <button type="button" className="secondary" onClick={() => void loadRelease()}>
            Retry
          </button>
        </div>
      )}

      {cardState === 'ready' && release && (
        <div className="bridge-release-info">
          <p>
            <strong>Version:</strong> {release.version}
            {' · '}
            <strong>Size:</strong> {formatBytes(release.size)}
          </p>
          <div className="bridge-checksum-row">
            <span className="bridge-sha256">
              <strong>SHA-256:</strong> <code>{release.sha256}</code>
            </span>
            {' '}
            <button type="button" className="secondary" onClick={handleCopyChecksum}>
              {copied ? 'Copied' : 'Copy checksum'}
            </button>
          </div>

          {isUnsignedBeta && (
            <p className="notice">
              This is a beta build. Windows may show a SmartScreen warning: choose More info, then Run anyway, after checking the checksum above.
            </p>
          )}

          <div className="bridge-install-steps">
            <p>
              Next steps: run the installer, then pair it from Settings or the <Link to="/devices">Devices page</Link>.
            </p>
          </div>

          {downloadError && (
            <p className="error" role="alert">{downloadError}</p>
          )}

          <div className="settings-actions">
            <button
              type="button"
              disabled={downloading}
              onClick={() => void handleDownload()}
            >
              {downloading ? 'Starting download…' : 'Download'}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
